import { Badge, Button, Tabs, useToast } from '../../components/primitives';
import { ListPage, ListTable, type ListColumn } from '../../components/list';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useDocuments,
  useDeleteDocument,
  usePodepsaneDokumenty,
  useDeletePodepsany,
  useStavby,
} from '../../api/hooks';
import type { PodepsanyDokument, SavedDocument } from '../../api/schemas';
import { apiFetch } from '../../lib/api';
import { SendEmailModal } from '../../components/SendEmailModal';
import { blobToBase64, renderDocHtml } from '../../lib/docBundle';
import { safeFileName } from '../../lib/safeFileName';

/**
 * Archiv uložených dokumentů — napojen na /api/documents.
 * Filtruje se přes query ?doc_type=.
 *
 * Kliknutí na řádek (nebo „Otevřít") přejde na příslušný formulář s ?id=,
 * kde se dokument předvyplní a lze ho znovu upravit, uložit, odeslat i stáhnout
 * (PDF / e-mail jsou součástí formuláře).
 *
 * Podepsané dokumenty (/app/podepsat) mají vlastní tabulku
 * (/api/podepsane-dokumenty) — do archivu se přimíchávají jako typ „podepsany",
 * náhled ukáže podepsané PDF (případně originál), úprava otevře editor podpisu.
 */

const docTypeMap: Record<
  string,
  { tone: 'yellow' | 'success' | 'neutral' | 'danger'; label: string; path?: string }
> = {
  predavaci: { tone: 'yellow', label: 'Předávací protokol — staveniště' },
  'predavaci-dilo': { tone: 'yellow', label: 'Předávací protokol — dílo' },
  'predavaci-dilo-velky': { tone: 'yellow', label: 'Předávací protokol — dílo (velký)' },
  faktura: { tone: 'success', label: 'Faktura' },
  zjistovaci: { tone: 'yellow', label: 'Zjišťovací protokol' },
  nabidka: { tone: 'neutral', label: 'Cenová nabídka' },
  objednavka: { tone: 'neutral', label: 'Objednávka' },
  reklamace: { tone: 'danger', label: 'Reklamace' },
  soupis: { tone: 'yellow', label: 'Soupis prací' },
  kosilka: { tone: 'neutral', label: 'Košilka' },
  trauma: { tone: 'danger', label: 'Traumatologický plán', path: '/app/bozp/trauma' },
  rizika: { tone: 'danger', label: 'Registr rizik', path: '/app/bozp/rizika' },
  'pozarni-dohled': { tone: 'danger', label: 'Požární dohled', path: '/app/bozp/pozarni-dohled' },
  podepsany: { tone: 'success', label: 'Podepsaný dokument' },
};

/** Řádek archivu — buď uložený formulář, nebo podepsaný (nahraný) dokument. */
type ArchivRow =
  | { kind: 'doc'; key: string; doc_type: string; title: string; stavba: string; created_at: string; updated_at: string; doc: SavedDocument }
  | { kind: 'signed'; key: string; doc_type: 'podepsany'; title: string; stavba: string; created_at: string; updated_at: string; signed: PodepsanyDokument };

/** Cesta formuláře pro daný typ — výchozí `/app/{doc_type}`, jinak `path` z mapy. */
function docPath(docType: string): string {
  return docTypeMap[docType]?.path ?? `/app/${docType}`;
}

/**
 * Typy dokumentů, které mají vlastní formulář pro úpravu. doc_type je zároveň
 * cesta routy (/app/{doc_type}), takže otevření je `/app/${doc_type}?id=${id}`.
 */
const editableTypes = new Set(Object.keys(docTypeMap));

const tabs = [
  { id: 'all', label: 'Vše' },
  { id: 'predavaci', label: 'Předávačky' },
  { id: 'faktura', label: 'Faktury' },
  { id: 'nabidka', label: 'Nabídky' },
  { id: 'objednavka', label: 'Objednávky' },
  { id: 'reklamace', label: 'Reklamace' },
  { id: 'podepsany', label: 'Podepsané' },
];

function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('cs-CZ');
}

export function ArchivPage() {
  const [tab, setTab] = useState('all');
  // Záložka „Předávačky" sdružuje všechny tři typy protokolů (staveniště, dílo,
  // dílo velký). Backend filtruje `doc_type` na přesnou shodu, takže je stáhneme
  // bez filtru a sloučíme podle prefixu `predavaci` na klientu.
  const isPredavaciTab = tab === 'predavaci';
  const isSignedTab = tab === 'podepsany';
  const docType = tab === 'all' || isPredavaciTab || isSignedTab ? undefined : tab;
  const docsQuery = useDocuments(docType ? { doc_type: docType } : undefined);
  const signedQuery = usePodepsaneDokumenty();
  const { data: stavby = [] } = useStavby();
  const fetched = docsQuery.data ?? [];

  const docRows: ArchivRow[] = (isPredavaciTab ? fetched.filter((r) => r.doc_type.startsWith('predavaci')) : fetched).map(
    (d) => ({
      kind: 'doc',
      key: `d${d.id}`,
      doc_type: d.doc_type,
      title: d.title,
      stavba: d.stavba,
      created_at: d.created_at,
      updated_at: d.updated_at,
      doc: d,
    }),
  );
  const signedRows: ArchivRow[] = (signedQuery.data ?? []).map((s) => ({
    kind: 'signed',
    key: `s${s.id}`,
    doc_type: 'podepsany',
    title: s.nazev || s.original_filename,
    stavba: s.stavba_id ? (stavby.find((x) => x.id === s.stavba_id)?.nazev ?? '') : '',
    created_at: s.created_at,
    updated_at: s.signed_at ?? s.created_at,
    signed: s,
  }));

  // „Vše" = formuláře + podepsané, seřazeno podle poslední úpravy (nejnovější nahoře).
  const rows: ArchivRow[] = isSignedTab
    ? signedRows
    : tab === 'all'
      ? [...docRows, ...signedRows].sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''))
      : docRows;

  const isLoading = isSignedTab ? signedQuery.isLoading : docsQuery.isLoading || (tab === 'all' && signedQuery.isLoading);
  const error = isSignedTab ? signedQuery.error : docsQuery.error || (tab === 'all' ? signedQuery.error : null);

  const navigate = useNavigate();
  const del = useDeleteDocument();
  const delSigned = useDeletePodepsany();
  const toast = useToast();
  const [emailRow, setEmailRow] = useState<ArchivRow | null>(null);
  // HTML dokumentu se začne připravovat hned po kliknutí na @ (běží na pozadí,
  // zatímco uživatel vyplňuje adresu); „Odeslat" na něj jen počká.
  const [emailHtml, setEmailHtml] = useState<Promise<string> | null>(null);

  function emailRowOpen(r: ArchivRow) {
    if (r.kind === 'doc') {
      if (!editableTypes.has(r.doc_type)) {
        toast.error('Tento typ dokumentu nelze z archivu odeslat.');
        return;
      }
      const p = renderDocHtml(`${docPath(r.doc_type)}?id=${r.doc.id}&pdf=html`, r.doc.id);
      p.catch(() => undefined); // chybu ukáže až dialog při odeslání
      setEmailHtml(p);
    } else {
      setEmailHtml(null);
    }
    setEmailRow(r);
  }

  async function signedAttachment(s: PodepsanyDokument) {
    const blob = await apiFetch<Blob>(`/podepsane-dokumenty/${s.id}/${s.is_signed ? 'signed' : 'original'}`);
    const filename = s.is_signed
      ? safeFileName(`${s.nazev || 'dokument'}_podepsano`)
      : s.original_filename || safeFileName(s.nazev || 'dokument');
    return { base64: await blobToBase64(blob), filename };
  }

  function openRow(r: ArchivRow) {
    if (r.kind === 'signed') navigate(`/app/podepsat?open=${r.signed.id}`);
    else openDoc(r.doc);
  }

  function openDoc(d: SavedDocument) {
    if (!editableTypes.has(d.doc_type)) {
      toast.error('Tento typ dokumentu nelze v této verzi otevřít.');
      return;
    }
    navigate(`${docPath(d.doc_type)}?id=${d.id}`);
  }

  /**
   * Náhled PDF — otevře dokument v nové záložce s `?pdf=preview`. Ta se sama
   * vyrenderuje, vygeneruje PDF přes backend a zobrazí ho inline.
   */
  function previewDoc(d: SavedDocument) {
    if (!editableTypes.has(d.doc_type)) {
      toast.error('Pro tento typ dokumentu náhled není k dispozici.');
      return;
    }
    const url = `${docPath(d.doc_type)}?id=${d.id}&pdf=preview`;
    // Bez 'noopener' — s ním vrací Chrome null i u nezablokovaného okna.
    const w = window.open(url, '_blank');
    if (!w) navigate(url); // fallback, když prohlížeč blokuje nové okno
  }

  /** Náhled podepsaného dokumentu — podepsané PDF, u nepodepsaného originál. */
  async function previewSigned(s: PodepsanyDokument) {
    // Okno otevřít hned (synchronně s klikem), jinak ho prohlížeč zablokuje.
    const w = window.open('', '_blank');
    try {
      const blob = await apiFetch<Blob>(`/podepsane-dokumenty/${s.id}/${s.is_signed ? 'signed' : 'original'}`);
      const url = URL.createObjectURL(blob);
      if (w) w.location.href = url;
      else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      w?.close();
      toast.error(e instanceof Error ? `Náhled selhal: ${e.message}` : 'Náhled selhal.');
    }
  }

  function previewRow(r: ArchivRow) {
    if (r.kind === 'signed') void previewSigned(r.signed);
    else previewDoc(r.doc);
  }

  async function deleteRow(r: ArchivRow) {
    if (r.kind === 'doc') return deleteDoc(r.doc);
    if (!window.confirm(`Smazat „${r.title}" (originál i podepsanou verzi)? Tuto akci nelze vrátit.`)) return;
    try {
      await delSigned.mutateAsync(r.signed.id);
      toast.success('Dokument smazán');
    } catch {
      toast.error('Smazání se nezdařilo');
    }
  }

  async function deleteDoc(d: SavedDocument) {
    if (!window.confirm(`Smazat dokument „${d.title}"? Tuto akci nelze vrátit.`)) return;
    try {
      await del.mutateAsync(d.id);
      toast.success('Dokument smazán');
    } catch {
      toast.error('Smazání se nezdařilo');
    }
  }

  // Sloupce drženy úzké + pružné „Název"/„Stavba", aby se tabulka vždy vešla
  // do šířky obrazovky bez vodorovného posuvníku (ListTable `fluid`). Interní
  // sloupec ID vynechán — pro fit a protože nenese obchodní informaci.
  const canOpen = (r: ArchivRow) => r.kind === 'signed' || editableTypes.has(r.doc_type);

  const columns: ListColumn<ArchivRow>[] = [
    {
      key: 'doc_type',
      label: 'Typ',
      width: '170px',
      render: (d) => {
        if (d.kind === 'signed' && !d.signed.is_signed) return <Badge tone="neutral">K podpisu (nepodepsáno)</Badge>;
        const m = docTypeMap[d.doc_type];
        return <Badge tone={m?.tone ?? 'neutral'}>{m?.label ?? d.doc_type}</Badge>;
      },
    },
    { key: 'title', label: 'Název', primary: true },
    { key: 'stavba', label: 'Stavba', dim: true, render: (d) => d.stavba || '—' },
    { key: 'created', label: 'Vytvořeno', width: '104px', mono: true, dim: true, render: (d) => formatDate(d.created_at) },
    { key: 'updated', label: 'Upraveno', width: '104px', mono: true, dim: true, render: (d) => formatDate(d.updated_at) },
  ];

  return (
    <ListPage
      breadcrumb={['Obchod', 'Archiv']}
      title="Archiv dokumentů"
      subtitle={
        isLoading
          ? 'Načítám…'
          : error
            ? 'Chyba při načítání'
            : `${rows.length} uložených dokumentů${tab !== 'all' ? ' v kategorii' : ''}`
      }
      status={isLoading ? 'neutral' : error ? 'draft' : 'done'}
      statusText={isLoading ? 'Načítám…' : error ? 'Chyba' : `${rows.length} dokumentů`}
      searchPlaceholder="Hledat podle názvu…"
      tools={<Tabs items={tabs} value={tab} onChange={setTab} />}
    >
      {error ? (
        <div style={{ padding: 24, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)' }}>
          Chyba: {error instanceof Error ? error.message : 'neznámá'}
        </div>
      ) : (
        <ListTable
          columns={columns}
          rows={rows}
          rowKey={(d) => d.key}
          onRowClick={openRow}
          fluid
          actionsWidth="168px"
          rowActions={(d) => (
            <>
              <Button
                size="sm"
                variant="ghost"
                title="Náhled PDF"
                disabled={!canOpen(d)}
                onClick={() => previewRow(d)}
              >
                👁
              </Button>
              <Button
                size="sm"
                variant="ghost"
                title="Poslat e-mailem"
                aria-label="Poslat e-mailem"
                disabled={!canOpen(d)}
                onClick={() => emailRowOpen(d)}
              >
                @
              </Button>
              <Button
                size="sm"
                variant="ghost"
                title="Otevřít / upravit"
                disabled={!canOpen(d)}
                onClick={() => openRow(d)}
              >
                ✎
              </Button>
              <Button
                size="sm"
                variant="ghost"
                title="Smazat"
                disabled={del.isPending || delSigned.isPending}
                onClick={() => void deleteRow(d)}
              >
                🗑
              </Button>
            </>
          )}
          emptyTitle="Zatím žádné uložené dokumenty"
          emptyText="Když dokument uložíte, objeví se zde."
        />
      )}
      {emailRow && (
        <SendEmailModal
          open
          onClose={() => {
            setEmailRow(null);
            setEmailHtml(null);
          }}
          docType={emailRow.kind === 'doc' ? emailRow.doc_type : 'podepsany'}
          docTitle={
            emailRow.kind === 'signed' && emailRow.signed.is_signed
              ? `${emailRow.title} – podepsáno`
              : emailRow.title || 'Dokument'
          }
          meta={emailRow.stavba ? `Stavba: ${emailRow.stavba}` : undefined}
          buildHtml={emailRow.kind === 'doc' && emailHtml ? () => emailHtml : undefined}
          buildAttachment={emailRow.kind === 'signed' ? () => signedAttachment(emailRow.signed) : undefined}
        />
      )}
    </ListPage>
  );
}
